import { apiClient } from '@/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const queryKeyImporters = 'importers'

export type Importer = {
  id: string
  customId?: string
  name: string
  identificationNumber: string
  placeOfRegistration: string
  street?: string
  street2?: string
  streetNumber?: string
  subDivision?: string
  country: string
  postCode?: string
  poBox?: string
  city?: string
  createdAt: Date
  updatedAt?: Date
  organizationId: string
}

type CreateImporterPayload = {
  name: string
  customId?: string
  identificationNumber: string
  placeOfRegistration: string
  street?: string
  street2?: string
  streetNumber?: string
  subDivision?: string
  country?: string
  postCode?: string
  poBox?: string
  city?: string
}

type UpdateImporterPayload = CreateImporterPayload

export type ValidationErrorMessages =
  | 'CUSTOM_ID_ALREADY_EXISTS'
  | 'IDENTIFICATION_NUMBER_ALREADY_EXISTS'

export type ValidationErrorResponse = {
  message: string
  errors: {
    message: ValidationErrorMessages
    field: string
    value: string
  }[]
}

export const useGetAllImporters = (organizationId: string) => {
  return useQuery({
    queryKey: [queryKeyImporters, organizationId],
    queryFn: async () => {
      const response = await apiClient.get<Importer[]>(
        `/v2/organizations/${organizationId}/importers`
      )
      return response.data
    }
  })
}

export const useCreateImporter = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CreateImporterPayload) => {
      const response = await apiClient.post<Importer>(
        `/v2/organizations/${organizationId}/importers`,
        payload
      )

      queryClient.setQueryData<Importer[]>([queryKeyImporters, organizationId], oldData => [
        ...(oldData || []),
        response.data
      ])
      return response.data
    }
  })
}

export const useUpdateImporter = (organizationId: string, importerId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateImporterPayload) => {
      const response = await apiClient.put<Importer>(
        `/v2/organizations/${organizationId}/importers/${importerId}`,
        payload
      )

      queryClient.setQueryData<Importer[]>(
        [queryKeyImporters, organizationId],
        oldData =>
          oldData?.map(importer => (importer.id === importerId ? response.data : importer)) || []
      )
      return response.data
    }
  })
}

export const useDeleteImporter = (organizationId: string, importerId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/v2/organizations/${organizationId}/importers/${importerId}`)

      queryClient.setQueryData<Importer[]>(
        [queryKeyImporters, organizationId],
        oldData => oldData?.filter(importer => importer.id !== importerId) || []
      )
    }
  })
}
